import React, { useState, useRef, useEffect } from 'react';
import { RunBtn } from '../../components/ui';

function randn_bm(){ let u=0,v=0; while(u===0) u=Math.random(); while(v===0) v=Math.random(); return Math.sqrt(-2.0*Math.log(u))*Math.cos(2.0*Math.PI*v); }

function Sparkline({data, color='#f59e0b', width=300, height=48}){
  if (!data || data.length===0) return <div style={{ width, height, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--t3)' }}>—</div>;
  const vals = data.slice(Math.max(0,data.length-120));
  const min = Math.min(...vals); const max = Math.max(...vals); const range = Math.max(1e-6, max-min); const step = width / Math.max(1, vals.length-1);
  const points = vals.map((v,i)=> `${i*step},${height - ((v-min)/range) * height}` ).join(' ');
  return (<svg width={width} height={height}><polyline fill="none" stroke={color} strokeWidth="2" points={points} strokeLinecap="round" strokeLinejoin="round" /></svg>);
}

export default function ExplorationLab(){
  const DEFAULT_ARMS = 5;
  const [armsN, setArmsN] = useState(DEFAULT_ARMS);
  const trueMeansRef = useRef(Array.from({length:DEFAULT_ARMS}).map(()=> (Math.random()*1.2) ));
  const countsRef = useRef(new Array(DEFAULT_ARMS).fill(0));
  const valuesRef = useRef(new Array(DEFAULT_ARMS).fill(0));
  const [policy, setPolicy] = useState('epsilon'); // 'epsilon' | 'ucb'
  const [epsilon, setEpsilon] = useState(0.2);
  const [decay, setDecay] = useState(0.995);
  const [cUCB, setCUCB] = useState(1.0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [running, setRunning] = useState(false);
  const [rewardLog, setRewardLog] = useState([]);
  const intervalRef = useRef(null);

  useEffect(()=>{
    // reset when arms change
    trueMeansRef.current = Array.from({length:armsN}).map(()=> (Math.random()*1.2));
    countsRef.current = new Array(armsN).fill(0);
    valuesRef.current = new Array(armsN).fill(0);
    setTotalSteps(0); setRewardLog([]);
  }, [armsN]);

  function pull(arm){ const mean = trueMeansRef.current[arm]; const r = mean + 0.15*randn_bm(); return r; }

  function selectAction(){
    const counts = countsRef.current; const values = valuesRef.current; const t = Math.max(1, counts.reduce((a,b)=>a+b,0));
    if (policy === 'epsilon' && Math.random() < epsilon){ return Math.floor(Math.random()*armsN); }
    if (policy === 'ucb'){
      let best=0; let bestv=-1e9;
      for(let a=0;a<armsN;a++){
        const c = counts[a]||1; const bonus = cUCB * Math.sqrt(Math.log(t+1)/c); const val = values[a] + bonus; if (val>bestv){ bestv=val; best=a }
      }
      return best;
    }
    // greedy
    let best=0; for(let a=1;a<armsN;a++) if ((values[a]||0) > (values[best]||0)) best=a; return best;
  }

  function stepOnce(){ const a = selectAction(); const r = pull(a); countsRef.current[a] += 1; const n = countsRef.current[a]; const old = valuesRef.current[a]; valuesRef.current[a] = old + (r - old)/n; setTotalSteps(s=>s+1); setRewardLog(l => [r].concat(l).slice(0,500)); if (policy==='epsilon') setEpsilon(e => Math.max(0.01, e*decay)); }

  function runToggle(){ if (running){ clearInterval(intervalRef.current); intervalRef.current = null; setRunning(false); } else { intervalRef.current = setInterval(stepOnce, 40); setRunning(true); } }

  function reset(){ trueMeansRef.current = Array.from({length:armsN}).map(()=> (Math.random()*1.2)); countsRef.current = new Array(armsN).fill(0); valuesRef.current = new Array(armsN).fill(0); setTotalSteps(0); setRewardLog([]); setEpsilon(0.2); }

  function exportJSON(){ const payload = { armsN, trueMeans: trueMeansRef.current, counts: countsRef.current, values: valuesRef.current }; const blob = new Blob([JSON.stringify(payload,null,2)], {type:'application/json'}); const url = URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='bandit_state.json'; a.click(); URL.revokeObjectURL(url); }

  return (
    <div style={{ padding: 18 }}>
      <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 800 }}>Exploration Lab — Multi-armed Bandit</div>
      <div style={{ marginTop: 6, color: 'var(--t2)' }}>Compare ε-greedy (with decay) vs UCB on a stochastic bandit. Visualize rewards and estimates.</div>

      <div style={{ display: 'flex', gap: 18, marginTop: 12 }}>
        <div style={{ minWidth: 420 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <label style={{ fontSize: 12 }}>Arms <input type="number" min={2} max={12} value={armsN} onChange={e=>setArmsN(Math.max(2, Math.min(12, parseInt(e.target.value)||DEFAULT_ARMS)))} style={{ width:64, marginLeft:6 }} /></label>
            <label style={{ fontSize: 12 }}>Policy
              <select value={policy} onChange={e=>setPolicy(e.target.value)} style={{ marginLeft:6 }}>
                <option value="epsilon">ε-greedy</option>
                <option value="ucb">UCB</option>
                <option value="greedy">Greedy</option>
              </select>
            </label>
          </div>

          <div style={{ display:'flex', gap:8, marginBottom:8 }}>
            <label style={{ fontSize: 12 }}>ε <input type="range" min="0" max="1" step="0.01" value={epsilon} onChange={e=>setEpsilon(parseFloat(e.target.value))} /></label>
            <label style={{ fontSize: 12 }}>Decay <input type="range" min="0.9" max="1" step="0.001" value={decay} onChange={e=>setDecay(parseFloat(e.target.value))} /></label>
            <label style={{ fontSize: 12 }}>UCB c <input type="range" min="0" max="3" step="0.1" value={cUCB} onChange={e=>setCUCB(parseFloat(e.target.value))} /></label>
          </div>

          <div style={{ display:'flex', gap:8, marginBottom:8, alignItems: 'center' }}>
            <button onClick={stepOnce} style={{ padding:'8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>STEP</button>
            <RunBtn running={running} onRun={() => runToggle()} onStop={() => runToggle()} label="RUN" />
            <button onClick={reset} style={{ padding:'8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>RESET</button>
            <button onClick={exportJSON} style={{ padding:'8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>EXPORT</button>
          </div>

          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize:12, color:'var(--t3)' }}>Total steps: <strong style={{ color:'var(--t1)' }}>{totalSteps}</strong> · Current ε: {epsilon.toFixed(3)}</div>
            <div style={{ marginTop:8, display:'flex', gap:12 }}>
              <div style={{ minWidth: 220 }}>
                <div style={{ fontSize:12, color:'var(--t3)', marginBottom:6 }}>Recent Rewards</div>
                <div style={{ border:'1px solid var(--b0)', padding:6, borderRadius:4 }}><Sparkline data={rewardLog} color="#f59e0b" /></div>
              </div>
              <div>
                <div style={{ fontSize:12, color:'var(--t3)', marginBottom:6 }}>True Means</div>
                <div style={{ display:'flex', gap:8 }}>
                  {trueMeansRef.current.map((m,i)=>(<div key={i} style={{ width:40, textAlign:'center' }}>{m.toFixed(2)}</div>))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex:1, maxWidth:420 }}>
          <div style={{ fontWeight:700, marginBottom:6 }}>Estimated Values & Counts</div>
          <div style={{ border:'1px solid var(--b0)', padding:10, borderRadius:6 }}>
            {valuesRef.current.map((v,i)=>(
              <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <div style={{ fontSize:13, color:'var(--t1)' }}>Arm {i}</div>
                <div style={{ fontSize:13, color:'var(--t3)' }}>{v.toFixed(3)} · n={countsRef.current[i]||0}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
